import { action, makeObservable, observable, runInAction } from 'mobx';

import { inject, injectable } from '@servicetitan/react-ioc';
import { InMemoryDataSource, TableState } from '@servicetitan/table';

import { APP_API_TOKEN, IAppApi, WebhookEventModelWithStatus } from '../../api/app.api';
import { NotificationCenterStore } from '../../common';
import { getDisplayNameString } from '../utils';

type WebhookEventViewModel = WebhookEventModelWithStatus & { displayStatus: string };

@injectable()
export class WebhookManagementPageStore {
    @observable id = 0;
    @observable publicId = '';
    @observable appName = '';
    @observable errorBannerVisible = false;
    @observable loading = true;
    @observable tableState = new TableState<WebhookEventModelWithStatus, string>({ pageSize: 25 });

    constructor(
        @inject(APP_API_TOKEN)
        private readonly appApi: IAppApi,
        @inject(NotificationCenterStore)
        private readonly notificationCenterStore: NotificationCenterStore,
    ) {
        makeObservable(this);
    }

    @action
    async load(id: number): Promise<void> {
        this.id = id;
        this.publicId = '';
        this.appName = '';
        this.errorBannerVisible = false;
        this.loading = true;
        try {
            const [model, webhookEvents] = await Promise.all([
                this.appApi.getApp(id),
                this.appApi.getWebhookEvents(id),
            ]);
            runInAction(() => {
                this.publicId = model.publicId;
                this.appName = model.appName;
                this.setWebhookEventsDataSource(webhookEvents.webhookEventsWithStatus);
                this.loading = false;
            });
        } catch {
            runInAction(() => {
                this.errorBannerVisible = true;
                this.loading = false;
            });
        }
    }

    @action
    async enableWebhook(webhookEvent: WebhookEventModelWithStatus): Promise<void> {
        await this.appApi.enableWebhookEvent(this.id, webhookEvent.name);
        this.notificationCenterStore.addSnackbar(
            `Webhook event ${webhookEvent.displayName} enabled.`,
        );
        await this.refreshWebhookEvents();
    }

    private async refreshWebhookEvents(): Promise<void> {
        try {
            const webhookEvents = await this.appApi.getWebhookEvents(this.id);
            runInAction(() => {
                this.setWebhookEventsDataSource(webhookEvents.webhookEventsWithStatus);
            });
        } catch {
            runInAction(() => {
                this.errorBannerVisible = true;
            });
        }
    }

    private setWebhookEventsDataSource(webhookEvents: WebhookEventModelWithStatus[]): void {
        const mapped: WebhookEventViewModel[] = webhookEvents.map(webhookEvent => ({
            ...webhookEvent,
            displayName: getDisplayNameString(webhookEvent),
            displayStatus: webhookEvent.accessible !== true ? 'Restricted' : webhookEvent.status,
        }));
        this.tableState.setDataSource(
            new InMemoryDataSource(
                this.sortWebhookEvents(mapped),
                webhookEvent => webhookEvent.name,
            ),
        );
    }

    /*
     * Sorts webhook events by status, with 'Disabled' events appearing first
     * and the rest sorted alphabetically. Use event display name as secondary sort key.
     */
    private sortWebhookEvents(webhookEvents: WebhookEventViewModel[]): WebhookEventViewModel[] {
        return webhookEvents.sort(
            (a, b) =>
                Number(a.displayStatus !== 'Disabled') - Number(b.displayStatus !== 'Disabled') ||
                a.displayStatus.localeCompare(b.displayStatus) ||
                a.displayName.localeCompare(b.displayName),
        );
    }
}
